import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";

export default function MovementForm({
  open,
  onOpenChange,
  defaultProductId,
  defaultType = "entrata",
}) {
  const { products, registerMovement } = useApp();
  const [form, setForm] = useState({
    product_id: "",
    type: "entrata",
    quantity: 1,
    note: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        product_id: defaultProductId || products[0]?.id || "",
        type: defaultType,
        quantity: 1,
        note: "",
      });
    }
  }, [open, defaultProductId, defaultType, products]);

  const selected = products.find((p) => p.id === form.product_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) {
      toast.error("Seleziona un prodotto");
      return;
    }
    const qty = Number(form.quantity);
    if (!qty || qty <= 0) {
      toast.error("La quantità deve essere maggiore di zero");
      return;
    }
    setSaving(true);
    try {
      await registerMovement({
        product_id: form.product_id,
        type: form.type,
        quantity: qty,
        note: form.note?.trim() || "",
      });
      toast.success(
        form.type === "entrata"
          ? `Entrata registrata: +${qty} ${selected?.unit || ""}`
          : `Uscita registrata: -${qty} ${selected?.unit || ""}`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="movement-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>Registra Movimento</DialogTitle>
          <DialogDescription>
            Registra un'entrata di merce dal fornitore o un'uscita per
            consumo/vendita.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, type: "entrata" }))}
              className={`flex items-center justify-center gap-2 rounded border px-3 py-3 text-sm font-semibold transition-colors ${
                form.type === "entrata"
                  ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="movement-type-entrata"
            >
              <ArrowDownToLine className="w-4 h-4" />
              Entrata
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, type: "uscita" }))}
              className={`flex items-center justify-center gap-2 rounded border px-3 py-3 text-sm font-semibold transition-colors ${
                form.type === "uscita"
                  ? "border-red-500 bg-red-50 text-red-900"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="movement-type-uscita"
            >
              <ArrowUpFromLine className="w-4 h-4" />
              Uscita
            </button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="m-product">Prodotto *</Label>
            <Select
              value={form.product_id}
              onValueChange={(v) => setForm((f) => ({ ...f, product_id: v }))}
            >
              <SelectTrigger id="m-product" data-testid="movement-product-select">
                <SelectValue placeholder="Seleziona prodotto" />
              </SelectTrigger>
              <SelectContent>
                {products.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-slate-500">
                    Nessun prodotto. Crea prima un prodotto.
                  </div>
                ) : (
                  products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} · {p.name} ({p.quantity} {p.unit})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="m-qty">
              Quantità * {selected && `(${selected.unit})`}
            </Label>
            <Input
              id="m-qty"
              type="number"
              min="1"
              step="1"
              value={form.quantity}
              onChange={(e) =>
                setForm((f) => ({ ...f, quantity: e.target.value }))
              }
              data-testid="movement-quantity-input"
              required
            />
            {selected && form.type === "uscita" && (
              <p className="text-xs text-slate-500">
                Giacenza attuale: <b>{selected.quantity}</b> {selected.unit}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="m-note">Nota (opzionale)</Label>
            <Textarea
              id="m-note"
              rows={2}
              placeholder="Es. Consegna DDT 1234, lotto XYZ..."
              value={form.note}
              onChange={(e) =>
                setForm((f) => ({ ...f, note: e.target.value }))
              }
              data-testid="movement-note-input"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="movement-form-cancel"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={saving || products.length === 0}
              className={
                form.type === "entrata"
                  ? "bg-emerald-700 hover:bg-emerald-800"
                  : "bg-red-700 hover:bg-red-800"
              }
              data-testid="movement-form-submit"
            >
              {saving
                ? "Registrazione..."
                : form.type === "entrata"
                  ? "Registra Entrata"
                  : "Registra Uscita"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
